import React from 'react';

const CustomerGroup: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Customer Group</h1>
          <p className="text-gray-600 mt-2">
            The Customer Group page in DAS allows you to organize and categorize your customers efficiently. Create hierarchical customer group structures with parent-child relationships to better manage your vendor relationships, streamline procurement processes, and improve customer analysis and reporting.
          </p>
        </div>

        {/* Main Customer Group List Image */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="mb-6">
            <img
              src="/images/sales/customer-group/customer-list.png"
              alt="Customer Group List Page"
              className="w-full max-w-4xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
          </div>
          <p className="text-gray-600 text-sm">
            <strong>Customer Group Overview:</strong> The main interface showing all customer groups with hierarchical structure. Parent groups like "Food Customer Group" are displayed with their sub-groups indented below, such as "Snack Customers" under the main group.
          </p>
        </div>

        {/* Customers Group Overview Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Customer Group Overview</h2>
          <p className="text-gray-600 mb-4">
            The Customer Group page provides a hierarchical view of your customer organization system. Groups can have parent-child relationships, allowing you to create detailed classification structures that match your procurement strategy and business needs.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Group Table Structure</h3>
          <p className="text-gray-600 mb-4">The customer groups table displays the following information:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Name:</strong> The group name with visual hierarchy indicators</li>
            <li><strong>Parent Customer Group:</strong> Shows the parent group for sub-groups (empty for top-level groups)</li>
            <li><strong>Actions:</strong> Quick access menu for editing, marking inactive, or deleting groups</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Hierarchical Display</h3>
          <p className="text-gray-600 mb-4">Groups are displayed with visual indicators showing their hierarchy:</p>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Parent Groups:</strong> Displayed at the top level (e.g., "Food Customer Group")</li>
            <li><strong>Sub-groups:</strong> Indented with connecting lines showing parent relationship (e.g., "Snack Customers")</li>
            <li><strong>Multiple Levels:</strong> Support for deep group structures as needed for complex customer categorization</li>
          </ul>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Example Group Structure</h3>
          <p className="text-gray-600 mb-4">Based on your current setup, typical group organization includes:</p>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
              <li><strong>Food Customer Group</strong> (Parent)
                <ul className="list-disc list-inside ml-4 mt-1 space-y-1">
                  <li>Snack Customers (Sub-group)</li>
                  <li>Beverage Customers (Sub-group)</li>
                  <li>Fresh Produce Customers (Sub-group)</li>
                </ul>
              </li>
              <li><strong>Equipment Customer Group</strong> (Parent)</li>
              <li><strong>Service Customer Group</strong> (Parent)</li>
            </ul>
          </div>
        </div>

        {/* Group Management Actions Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Group Management Actions</h2>
          <p className="text-gray-600 mb-4">
            Each customer group row includes a dropdown menu with management options:
          </p>

          <div className="mb-6">
            <img
              src="/images/sales/customer-group/edit.png"
              alt="Group Actions Menu"
              className="w-full max-w-2xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Group Actions:</strong> The dropdown menu appears when clicking the three dots icon, providing access to Edit, Mark As Inactive, and Delete options.
            </p>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Available Actions</h3>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Edit:</strong> Modify group name and parent group assignment</li>
            <li><strong>Mark As Inactive:</strong> Deactivate groups without deleting them</li>
            <li><strong>Delete:</strong> Permanently remove groups (with confirmation)</li>
          </ul>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <p className="text-sm text-yellow-800">
              <strong>Important:</strong> Deleting a parent group will affect all its sub-groups and assigned customers. Consider marking groups as inactive instead of deleting them to preserve historical data and customer relationships.
            </p>
          </div>
        </div>

        {/* Creating New Groups Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Creating New Customer Groups</h2>
          <p className="text-gray-600 mb-4">
            Adding new customer groups is straightforward using the <strong>+ New</strong> button in the Customer Group page.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Create Group Process</h3>
          <p className="text-gray-600 mb-4">To create a new customer group:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Click the <strong>+ New</strong> button in the top-right corner</li>
            <li>The "Create Customer Group" dialog will open with the following options:</li>
          </ol>

          <div className="mb-6">
            <img
              src="/images/sales/customer-group/new.png"
              alt="Create Customer Group Dialog"
              className="w-full max-w-3xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Create Customer Group Form:</strong> Modal dialog with group name field, "Make this a sub-group" checkbox, and parent group dropdown for creating new customer groups.
            </p>
          </div>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Form Fields</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Name:</strong> Enter the group name (required field)</li>
            <li><strong>Make this a sub-group:</strong> Checkbox to create a sub-group</li>
            <li><strong>Parent Customer Group:</strong> Select the parent group from dropdown (appears when sub-group is checked)</li>
          </ul>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Group Types</h4>
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <p className="text-gray-600 mb-2"><strong>Top-Level Groups:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Independent groups without parents</li>
                <li>Serve as main customer classifications</li>
                <li>Examples: "Food Customer Group", "Equipment Cutomers", "Service Providers"</li>
                <li>Leave sub-group checkbox unchecked</li>
              </ul>
            </div>
            <div>
              <p className="text-gray-600 mb-2"><strong>Sub-groups:</strong></p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 text-sm">
                <li>Groups that belong to a parent group</li>
                <li>Help create detailed customer classification</li>
                <li>Examples: "Snack Customers" under "Food Customer Group"</li>
                <li>Check sub-group box and select parent</li>
              </ul>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-green-800">
              <strong>Tip:</strong> The parent group dropdown shows existing groups, making it easy to select the correct parent and understand the hierarchy structure.
            </p>
          </div>

          <p className="text-gray-600 mb-4">After filling the form:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2">
            <li>Review the group name and parent selection</li>
            <li>Click <strong>Save</strong> to create the group</li>
            <li>Click <strong>Cancel</strong> to discard and close the dialog</li>
            <li>The new group will appear in the groups list with proper hierarchy</li>
          </ol>
        </div>

        {/* Editing Groups Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Editing Customer Groups</h2>
          <p className="text-gray-600 mb-4">
            Modify existing groups to reorganize your customer structure or update group names.
          </p>

          <h3 className="text-lg font-semibold text-gray-800 mb-3">Edit Group Process</h3>
          <p className="text-gray-600 mb-4">To edit an existing group:</p>
          <ol className="list-decimal list-inside text-gray-600 space-y-2 mb-4">
            <li>Click the actions menu (three dots) for the group you want to edit</li>
            <li>Select <strong>Edit</strong> from the dropdown menu</li>
            <li>The "Edit Customer Group" dialog will open with current information pre-filled:</li>
          </ol>

          <div className="mb-6">
            <img
              src="/images/sales/customer-group/editform.png"
              alt="Edit Customer Group Dialog"
              className="w-full max-w-3xl h-auto border border-gray-200 rounded-lg shadow-sm"
            />
            <p className="text-gray-600 text-sm mt-2">
              <strong>Edit Customer Group Form:</strong> Shows "Snack Customers" being edited with sub-group checkbox checked and "Food Customer Group" selected as parent group.
            </p>
          </div>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Editable Properties</h4>
          <ul className="list-disc list-inside text-gray-600 space-y-2 mb-4">
            <li><strong>Group Name:</strong> Update the display name</li>
            <li><strong>Sub-group Status:</strong> Convert between top-level and sub-group</li>
            <li><strong>Parent Assignment:</strong> Change the parent group or remove parent relationship</li>
          </ul>

          <h4 className="text-lg font-semibold text-gray-800 mb-3">Common Edit Scenarios</h4>
          <div className="space-y-3 mb-4">
            <div className="border-l-4 border-blue-500 pl-4">
              <p className="font-semibold text-gray-800">Renaming Groups</p>
              <p className="text-gray-600 text-sm">Update group names to better reflect your customer organization or business changes.</p>
            </div>

            <div className="border-l-4 border-green-500 pl-4">
              <p className="font-semibold text-gray-800">Reorganizing Hierarchy</p>
              <p className="text-gray-600 text-sm">Move groups between parents or convert sub-groups to top-level groups as your customer structure evolves.</p>
            </div>

            <div className="border-l-4 border-yellow-500 pl-4">
              <p className="font-semibold text-gray-800">Creating Sub-groups</p>
              <p className="text-gray-600 text-sm">Convert existing top-level groups into sub-groups by selecting a parent group.</p>
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> Changes to group structure will not affect existing customers assigned to these groups, but may impact how they appear in filtered views and reports.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerGroup;
